/**
 * Knowledge Base Help Content
 *
 * Covers documentation, wikis, policies, and organizational knowledge.
 */

import type { HelpContent } from '../types';
import {
  createHelpContent,
  createSection,
  createFAQ,
  createTip,
  createRelatedLink,
} from '../utils';

// =============================================================================
// MAIN KNOWLEDGE BASE HELP
// =============================================================================

export const knowledgeHelp: HelpContent = createHelpContent({
  id: 'knowledge',
  title: 'Knowledge Base',
  description: 'Documentation, wikis, policies, and organizational knowledge',
  lastUpdated: '2026-02-08',
  version: '2.0',

  tips: [
    createTip(
      'tip-tagging',
      'pro-tip',
      'Search Tips',
      'Use specific keywords related to your question. Try different terms if your first search doesn\'t return results. Filter by category to narrow down options.'
    ),
    createTip(
      'tip-bookmark',
      'info',
      'Bookmark Articles',
      'Click the bookmark icon on any article to save it for quick access later. Access your bookmarked articles from the bookmarks section.'
    ),
  ],

  sections: [
    createSection(
      'overview',
      'Overview',
      `<p>The Knowledge Base is your central repository for organizational documentation, policies, procedures, and institutional knowledge. Find answers, best practices, and important information all in one place.</p>
      <p>Content is organized into categories:</p>
      <ul>
        <li><strong>Policies</strong> — HR policies, code of conduct, security guidelines</li>
        <li><strong>Procedures</strong> — How-to guides and standard operating procedures</li>
        <li><strong>Technical Docs</strong> — Engineering documentation and architecture guides</li>
        <li><strong>Company Info</strong> — Organizational structure, mission, and values</li>
      </ul>`
    ),
    createSection(
      'browsing',
      'Browsing Articles',
      `<p>Navigate the knowledge base by:</p>
      <ul>
        <li><strong>Categories</strong> — Click category tabs to browse by topic</li>
        <li><strong>Recent</strong> — See recently updated articles</li>
        <li><strong>Popular</strong> — View most-accessed articles</li>
        <li><strong>Bookmarks</strong> — Access your saved articles</li>
      </ul>
      <p>Each article shows its title, category, last update date, and a brief summary.</p>`
    ),
    createSection(
      'search',
      'Search & Filters',
      `<p>Use the search bar to find specific information:</p>
      <ul>
        <li>Search by keywords, article title, or content</li>
        <li>Filter results by category or date range</li>
        <li>Results are ranked by relevance</li>
      </ul>
      <p>If search doesn't find what you need, try broader terms or browse by category.</p>`
    ),
    createSection(
      'contributing',
      'Contributing Knowledge',
      `<p>If you have permission to contribute, you can add or update knowledge base articles:</p>
      <ul>
        <li>Use clear, descriptive titles</li>
        <li>Organize content with headings and bullet points</li>
        <li>Keep information accurate and up-to-date</li>
        <li>Tag articles with relevant keywords for discoverability</li>
        <li>Link to related articles where appropriate</li>
      </ul>`
    ),
    createSection(
      'categories',
      'Categories',
      `<p>Articles are organized into categories to make navigation easier. Common categories include:</p>
      <ul>
        <li><strong>HR & People</strong> — Leave policies, benefits, onboarding guides</li>
        <li><strong>IT & Security</strong> — Password policies, VPN setup, security guidelines</li>
        <li><strong>Finance</strong> — Expense policies, procurement, budgets</li>
        <li><strong>Engineering</strong> — Technical documentation, coding standards, architecture</li>
        <li><strong>General</strong> — Company information, org structure, office guides</li>
      </ul>`
    ),
  ],

  faqs: [
    createFAQ(
      'faq-access',
      'How do I request access to restricted content?',
      'Some knowledge base articles may be restricted to certain teams or roles. Contact your manager or the content owner to request access. IT can also help with permission issues.',
      ['access', 'permissions', 'restricted', 'request']
    ),
    createFAQ(
      'faq-corrections',
      'Can I suggest corrections to an article?',
      'Yes! If you find outdated or incorrect information, click the feedback button on the article or contact the article owner directly. Your input helps keep the knowledge base accurate.',
      ['feedback', 'correction', 'update', 'suggest', 'wrong']
    ),
    createFAQ(
      'faq-bookmark',
      'How do I bookmark articles for later?',
      'Click the bookmark icon on any article to save it to your favorites. Access your bookmarked articles from your profile or the bookmarks section.',
      ['bookmark', 'save', 'favorite', 'later']
    ),
    createFAQ(
      'faq-maintain',
      'Who maintains the knowledge base content?',
      'Content is maintained by subject matter experts across teams. Each article shows its owner and last update date. For general questions about the knowledge base, contact IT or HR.',
      ['owner', 'maintain', 'update', 'responsible']
    ),
    createFAQ(
      'faq-not-found',
      'What if I can\'t find what I\'m looking for?',
      'If your search doesn\'t return results, try different keywords or browse by category. You can also ask a colleague or submit a request for new content to be added.',
      ['search', 'missing', 'find', 'help', 'not found']
    ),
    createFAQ(
      'faq-contribute',
      'How can I contribute an article?',
      'If you have author permissions, use the "Create Article" button to write a new article. Choose the appropriate category, write clear content, and add tags. Your article may need review before publishing.',
      ['contribute', 'write', 'create', 'new article', 'author']
    ),
  ],

  relatedLinks: [
    createRelatedLink('Training', '/training', { description: 'Learn new skills with structured courses' }),
    createRelatedLink('News', '/news', { description: 'Stay updated with company announcements' }),
    createRelatedLink('Directory', '/directory', { description: 'Find subject matter experts' }),
  ],

  keywords: [
    'knowledge',
    'wiki',
    'documentation',
    'policies',
    'procedures',
    'guides',
    'information',
    'articles',
    'FAQ',
    'help',
  ],
});
